import { Box, Typography, useTheme } from '@mui/material';
import { WaiverRequestStatus } from '@prisma/client';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { LightTooltip } from '../../global/LightTooltip';

interface Props {
  status: WaiverRequestStatus;
}

export default function WaiverStatusBadge({ status }: Props) {
  const theme = useTheme();

  let badgeText = '';
  let badgeIcon = null;
  let backgroundColor = '';
  let textColor = '';
  let tooltipText = '';
  let borderColor = '';

  switch (status) {
    case WaiverRequestStatus.CANCELED:
      badgeText = 'Canceled';
      backgroundColor = theme.palette.primary.light;
      textColor = theme.palette.primary.main;
      borderColor = '#FFC2B6';
      tooltipText = 'Waiver request was canceled';
      badgeIcon = (
        <CloseIcon
          sx={{
            color: theme.palette.primary.main,
            width: '16px',
            height: '16px',
          }}
        />
      );
      break;

    case WaiverRequestStatus.QUEUED:
      badgeText = 'Queued';
      backgroundColor = theme.palette.lightGrey.main;
      textColor = theme.palette.darkGrey.main;
      borderColor = theme.palette.lightOutlineGrey.main;
      tooltipText =
        'Waiver link will be sent to customer on the day of their appointment';
      badgeIcon = (
        <AccessTimeIcon
          sx={{
            color: theme.palette.darkGrey.main,
            width: '16px',
            height: '16px',
          }}
        />
      );
      break;

    case WaiverRequestStatus.SENT:
      badgeText = 'Sent';
      backgroundColor = '#fdf8c9';
      textColor = '#b13600';
      borderColor = '#fbd992';
      tooltipText = 'Waiver link has been sent to the customer';
      badgeIcon = (
        <AccessTimeIcon
          sx={{
            color: '#b13600',
            width: '16px',
            height: '16px',
          }}
        />
      );
      break;

    case WaiverRequestStatus.SIGNED:
      badgeText = 'Signed';
      backgroundColor = theme.palette.accent.main;
      textColor = theme.palette.secondary.main;
      borderColor = '#AAF2DA';
      tooltipText = 'Waiver has been signed by the customer';
      badgeIcon = (
        <CheckIcon
          sx={{
            color: theme.palette.secondary.main,
            width: '16px',
            height: '16px',
          }}
        />
      );
      break;

    default:
      badgeText = 'Queued';
      backgroundColor = theme.palette.lightGrey.main;
      textColor = theme.palette.darkGrey.main;
      borderColor = theme.palette.lightOutlineGrey.main;
      tooltipText =
        'Waiver link will be sent to customer on the day of their appointment';
      badgeIcon = (
        <AccessTimeIcon
          sx={{
            color: theme.palette.darkGrey.main,
            width: '16px',
            height: '16px',
          }}
        />
      );
      break;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <LightTooltip
        title={tooltipText}
        placement="bottom"
        arrow
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -10],
                },
              },
            ],
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '2px 6px',
            backgroundColor,
            borderRadius: '4px',
            gap: '4px',
            borderColor,
            borderWidth: '1px',
            borderStyle: 'solid',
            flexShrink: 1,
          }}>
          <Typography
            variant="h5"
            sx={{
              color: textColor,
              fontSize: '12px',
              lineHeight: '12px',
            }}>
            {badgeText}
          </Typography>
          {badgeIcon}
        </Box>
      </LightTooltip>
    </Box>
  );
}
